import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Code2, Mail, Lock, User, Sun, Moon, ArrowLeft, Award, BookOpen, GraduationCap, Globe } from 'lucide-react';
import { toast } from 'sonner';

const BOARDS = [
  { value: 'CBSE', label: 'CBSE', desc: 'Central Board of Secondary Education' },
  { value: 'ICSE', label: 'ICSE / ISC', desc: 'Indian Certificate of Secondary Education' },
  { value: 'SSC', label: 'SSC (State Board)', desc: 'Secondary School Certificate' },
  { value: 'HSC', label: 'HSC (State Board)', desc: 'Higher Secondary Certificate' },
];

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'Hindi' },
  { value: 'mr', label: 'Marathi' },
  { value: 'ta', label: 'Tamil' },
  { value: 'te', label: 'Telugu' },
  { value: 'kn', label: 'Kannada' },
  { value: 'ml', label: 'Malayalam' },
  { value: 'bn', label: 'Bengali' },
  { value: 'gu', label: 'Gujarati' },
  { value: 'pa', label: 'Punjabi' },
];

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'independent_student',
    grade: '',
    board: '',
    education_language: ''
  });
  const [loading, setLoading] = useState(false);
  const { register, loginWithGoogle } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isStudent = formData.role === 'independent_student';

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (isStudent && !formData.board) {
      toast.error('Please select your education board');
      return;
    }
    if (isStudent && !formData.grade) {
      toast.error('Please select your grade');
      return;
    }
    if (isStudent && !formData.education_language) {
      toast.error('Please select your language of education');
      return;
    }

    setLoading(true);
    try {
      const userData = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        ...(formData.grade && { grade: parseInt(formData.grade) }),
        ...(formData.board && { board: formData.board }),
        ...(formData.education_language && { education_language: formData.education_language }),
      };
      await register(userData);
      toast.success('Account created successfully!');
      // Phase 40.5 — students must complete the onboarding questionnaire
      // before landing on the dashboard.  Parents keep their existing flow.
      if (formData.role === 'parent') {
        navigate('/parent');
      } else {
        navigate('/onboarding');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    loginWithGoogle();
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-secondary relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtOS45NDEgMC0xOCA4LjA1OS0xOCAxOHM4LjA1OSAxOCAxOCAxOCAxOC04LjA1OSAxOC0xOC04LjA1OS0xOC0xOC0xOHptMCAzMmMtNy43MzIgMC0xNC02LjI2OC0xNC0xNHM2LjI2OC0xNCAxNC0xNCAxNCA2LjI2OCAxNCAxNC02LjI2OCAxNC0xNCAxNHoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjEiLz48L2c+PC9zdmc+')] opacity-20" />
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <div className="mb-8">
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center mb-6">
              <Award className="w-10 h-10" />
            </div>
            <h1 className="text-4xl font-bold mb-4">Start Your Journey</h1>
            <p className="text-lg opacity-90">
              Board-aligned courses for CBSE, ICSE, SSC & HSC. Interactive labs, exams, and AI-powered learning.
            </p>
          </div>
          <div className="space-y-4">
            {[
              { num: 1, title: 'Choose Your Board & Grade', desc: 'CBSE, ICSE, SSC, HSC supported' },
              { num: 2, title: 'Learn with AI Teacher', desc: 'Personalized avatar guides you' },
              { num: 3, title: 'Practice & Excel', desc: 'Board exams, competitive exams, labs' },
            ].map(s => (
              <div key={s.num} className="flex items-center gap-3 p-4 rounded-xl bg-white/10 backdrop-blur">
                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white font-bold">{s.num}</div>
                <div>
                  <div className="font-medium">{s.title}</div>
                  <div className="text-sm opacity-75">{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex flex-col">
        <div className="flex items-center justify-between p-6">
          <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <Button variant="ghost" size="icon" onClick={toggleTheme} className="rounded-full" data-testid="theme-toggle-register">
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
        </div>

        <div className="flex-1 flex items-center justify-center p-6 overflow-y-auto">
          <div className="w-full max-w-md space-y-5">
            <div className="lg:hidden flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <Code2 className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold">CognitiaIndia</span>
            </div>

            <div>
              <h2 className="text-2xl font-bold">Create your account</h2>
              <p className="text-muted-foreground mt-1 text-sm">
                Already have an account?{' '}
                <Link to="/login" className="text-primary hover:underline font-medium">Sign in</Link>
              </p>
            </div>

            <Button type="button" variant="outline" className="w-full h-11 rounded-lg" onClick={handleGoogleLogin} data-testid="google-register-btn">
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-background text-muted-foreground">Or continue with email</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3.5">
              {/* Name */}
              <div className="space-y-1.5">
                <Label htmlFor="name">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="name" placeholder="Your full name" value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className="pl-10 h-10 rounded-lg" required data-testid="name-input" />
                </div>
              </div>

              {/* Email */}
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="email" type="email" placeholder="you@example.com" value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="pl-10 h-10 rounded-lg" required data-testid="email-input" />
                </div>
              </div>

              {/* Role */}
              <div className="space-y-1.5">
                <Label>I am a</Label>
                <Select value={formData.role} onValueChange={(v) => handleChange('role', v)}>
                  <SelectTrigger className="h-10 rounded-lg" data-testid="role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="independent_student">Student</SelectItem>
                    <SelectItem value="faculty">Teacher</SelectItem>
                    <SelectItem value="parent">Parent</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Board, Grade, Language - Required for students */}
              {isStudent && (
                <div className="space-y-3.5 p-4 rounded-xl border border-border bg-muted/30">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Academic Details (Required)</p>

                  {/* Board */}
                  <div className="space-y-1.5">
                    <Label className="flex items-center gap-1.5">
                      <BookOpen className="w-3.5 h-3.5 text-blue-400" /> Education Board
                    </Label>
                    <Select value={formData.board} onValueChange={(v) => handleChange('board', v)}>
                      <SelectTrigger className="h-10 rounded-lg" data-testid="board-select">
                        <SelectValue placeholder="Select your board" />
                      </SelectTrigger>
                      <SelectContent>
                        {BOARDS.map(b => (
                          <SelectItem key={b.value} value={b.value}>
                            <span className="font-medium">{b.label}</span>
                            <span className="text-xs text-muted-foreground ml-2">{b.desc}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Grade */}
                  <div className="space-y-1.5">
                    <Label className="flex items-center gap-1.5">
                      <GraduationCap className="w-3.5 h-3.5 text-green-400" /> Grade / Class
                    </Label>
                    <Select value={formData.grade} onValueChange={(v) => handleChange('grade', v)}>
                      <SelectTrigger className="h-10 rounded-lg" data-testid="grade-select">
                        <SelectValue placeholder="Select your grade" />
                      </SelectTrigger>
                      <SelectContent>
                        {[1,2,3,4,5,6,7,8,9,10,11,12].map(g => (
                          <SelectItem key={g} value={String(g)}>Class {g}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Language */}
                  <div className="space-y-1.5">
                    <Label className="flex items-center gap-1.5">
                      <Globe className="w-3.5 h-3.5 text-purple-400" /> Language of Education
                    </Label>
                    <Select value={formData.education_language} onValueChange={(v) => handleChange('education_language', v)}>
                      <SelectTrigger className="h-10 rounded-lg" data-testid="language-select">
                        <SelectValue placeholder="Select language" />
                      </SelectTrigger>
                      <SelectContent>
                        {LANGUAGES.map(l => (
                          <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Password */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="password" type="password" placeholder="Min 6 chars" value={formData.password}
                      onChange={(e) => handleChange('password', e.target.value)}
                      className="pl-10 h-10 rounded-lg" required data-testid="password-input" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="confirmPassword">Confirm</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="confirmPassword" type="password" placeholder="Re-enter" value={formData.confirmPassword}
                      onChange={(e) => handleChange('confirmPassword', e.target.value)}
                      className="pl-10 h-10 rounded-lg" required data-testid="confirm-password-input" />
                  </div>
                </div>
              </div>

              <Button type="submit" className="w-full h-11 btn-primary rounded-lg text-sm font-semibold" disabled={loading} data-testid="register-submit-btn">
                {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Create Account'}
              </Button>
            </form>

            <p className="text-center text-xs text-muted-foreground">
              By creating an account, you agree to our Terms of Service and Privacy Policy.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
